import { useState } from "react";
import TsDepartmentPicker from "@/components/mobile/TsDepartmentPicker";
import { TS_DEPARTMENT_LABELS } from "@/components/mobile/TsDepartmentPicker";

const PINK = "#EE2677";

/**
 * Post-OTP TS onboarding: confirm name, show Partner badge, pick departments.
 * mode="review" — reopen from TS home without OTP.
 */
export default function TsOnboarding({
  identity,
  initialDepartments = [],
  mode = "setup",
  onComplete,
  onCancel,
  busy = false,
}) {
  const [name, setName] = useState(() => (identity?.name || "").trim());
  const available = identity?.available_departments || [];
  const isAdmin = Boolean(identity?.is_ts_admin);
  const employeeId = (identity?.employee_id || "").trim();
  const isReview = mode === "review";
  const heading = isReview ? "Review setup" : "Welcome";
  const sub = isReview
    ? "Update your name and departments. Your role is assigned by your manager."
    : "Confirm your name. Your role is assigned by your manager.";

  const defaultSelected = () => {
    if (initialDepartments.length) return initialDepartments.filter((d) => available.includes(d));
    if (available.length === 2) return [...available];
    return [];
  };

  const handleDepartments = (departments) => {
    onComplete?.({ name: name.trim(), departments });
  };

  const handleAdminOrSingleContinue = () => {
    handleDepartments(isAdmin ? available : available);
  };

  return (
    <div className="tb-onboard">
      <style>{`
        .tb-onboard {
          min-height: 100dvh; background: #F5F2EA; color: #2D2D2D;
          padding: 12px 14px 28px; max-width: 480px; margin: 0 auto;
          font-family: system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
        }
        .tb-onboard * { box-sizing: border-box; }
        .ts-onboard-brand { text-align: center; margin: 24px 0 20px; }
        .ts-onboard-kicker {
          font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
          text-transform: uppercase; color: rgba(45,45,45,0.45); margin: 0 0 4px;
        }
        .ts-onboard-title { font-size: 28px; font-weight: 900; color: ${PINK}; margin: 0; }
        .ts-onboard-card {
          background: #fff; border-radius: 20px; padding: 20px 18px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .ts-onboard-heading { font-size: 20px; font-weight: 800; margin: 0 0 6px; }
        .ts-onboard-sub {
          font-size: 14px; color: rgba(45,45,45,0.65); margin: 0 0 16px; line-height: 1.45;
        }
        .ts-onboard-badge {
          display: inline-block; margin-bottom: 14px; padding: 6px 12px;
          border-radius: 999px; background: #ECFDF5; color: #047857;
          font-size: 12px; font-weight: 800; letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .ts-onboard-badge--id {
          background: #EFF6FF; color: #1D4ED8;
        }
        .ts-onboard-field label {
          display: block; font-size: 13px; font-weight: 700; margin-bottom: 6px;
        }
        .ts-onboard-field input {
          width: 100%; border: 2px solid #E5E7EB; border-radius: 14px;
          padding: 14px; font-size: 17px; background: #fff;
        }
        .ts-onboard-section {
          margin-top: 20px; padding-top: 18px; border-top: 1px solid #F3F4F6;
        }
        .ts-onboard-section h3 {
          margin: 0 0 4px; font-size: 16px; font-weight: 800;
        }
        .ts-onboard-cancel {
          width: 100%; margin-top: 10px; border: none; background: transparent;
          color: rgba(45,45,45,0.55); font-size: 14px; font-weight: 700; cursor: pointer; padding: 8px;
        }
      `}</style>

      <div className="ts-onboard-brand">
        <p className="ts-onboard-kicker">The Table Top System</p>
        <h1 className="ts-onboard-title">{heading}</h1>
      </div>

      <div className="ts-onboard-card">
        <h2 className="ts-onboard-heading">Your profile</h2>
        <p className="ts-onboard-sub">{sub}</p>
        {employeeId ? (
          <span className="ts-onboard-badge ts-onboard-badge--id">Employee {employeeId}</span>
        ) : (
          <span className="ts-onboard-badge">Team staff</span>
        )}

        <div className="ts-onboard-field">
          <label htmlFor="ts-onboard-name">Display name</label>
          <input
            id="ts-onboard-name"
            type="text"
            autoComplete="name"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {isAdmin || available.length <= 1 || employeeId ? (
          <button
            type="button"
            className="ts-dept-picker-primary"
            style={{
              width: "100%",
              border: "none",
              borderRadius: 16,
              padding: 16,
              fontSize: 17,
              fontWeight: 800,
              color: "#fff",
              background: PINK,
              cursor: "pointer",
              marginTop: 16,
            }}
            disabled={busy || !name.trim()}
            onClick={handleAdminOrSingleContinue}
          >
            {busy ? "Saving…" : isReview ? "Save setup" : "Continue to TS"}
          </button>
        ) : (
          <div className="ts-onboard-section">
            <h3>Your departments</h3>
            <p className="ts-onboard-sub" style={{ marginBottom: 0 }}>
              Choose which apps appear on your TS home (
              {available.map((k) => TS_DEPARTMENT_LABELS[k] || k).join(", ")}).
            </p>
            <TsDepartmentPicker
              availableDepartments={available}
              initialSelected={defaultSelected()}
              minRequired={2}
              onConfirm={handleDepartments}
              busy={busy}
            />
          </div>
        )}

        {isReview && onCancel ? (
          <button type="button" className="ts-onboard-cancel" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </div>
  );
}
